import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { router } from './router';
import { ToastProvider } from './components/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LocaleProvider } from './i18n';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root element missing — check index.html');

// docs/plan/google-sso-auth-2026-06 — Google SSO. clientId 가 있을 때만
// GoogleOAuthProvider 로 감싼다(빈 값이면 provider 가 콘솔 에러를 내므로). 미설정
// 환경(로컬/CI)에서는 LoginPage 가 관리자(비번) 로그인으로 graceful fallback.
const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '';

const tree: ReactNode = (
  <ErrorBoundary>
    <LocaleProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </LocaleProvider>
  </ErrorBoundary>
);

createRoot(rootEl).render(
  <StrictMode>
    {googleClientId ? (
      <GoogleOAuthProvider clientId={googleClientId}>{tree}</GoogleOAuthProvider>
    ) : (
      tree
    )}
  </StrictMode>,
);
